<script lang="ts">
import { RouteNames } from "@/utils/constants.ts";

export function getRouteOnboardAccountType() {
  return {
    name: RouteNames.ONBOARDING_ACCOUNT_TYPE
  };
}
</script>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import CardLayout from "@/components/layout/CardLayout.vue";
import { getRouteXDashboard } from "@/views/Auth/Dashboard.vue";
import { getRouteOnboardPersonalProfile } from "@/views/Onboarding/OnboardingPersonalProfile.vue";

const router = useRouter();
const isLoading = ref(false);

async function selectIndividual() {
  isLoading.value = true;
  try {
    // Individual client is created automatically from personal profile
    // Navigate directly to Dashboard
    await router.push(getRouteXDashboard());
  } catch (e) {
    console.error('Error selecting individual account:', e);
  } finally {
    isLoading.value = false;
  }
}

async function selectBusiness() {
  isLoading.value = true;
  try {
    // For business onboarding, ALWAYS start with APPLICANT step (personal profile)
    // Add query parameter to indicate business context
    await router.push({ ...getRouteOnboardPersonalProfile(), query: { type: 'business' } });
  } catch (e) {
    console.error('Error selecting business account:', e);
  } finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <CardLayout
    title="Personal info"
    subtitle="Application for opening and maintaining a personal current account"
    heading="Account Type"
    :max-steps="4"
    :current-step="4"
    :back="getRouteXDashboard()"
  >
    <div class="flex flex-col items-center gap-8 w-full px-4 md:px-0">
      <div class="text-center space-y-2">
        <h2 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100">
          Choose Your Account Type
        </h2>
        <p class="text-sm md:text-base text-gray-600 dark:text-gray-400">
          Select the type of account you want to create
        </p>
      </div>

      <div class="w-full max-w-md space-y-4">
        <!-- Individual Account -->
        <button
          @click="selectIndividual"
          :disabled="isLoading"
          class="w-full group relative overflow-hidden rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 transition-all hover:border-blue-500 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div class="flex items-center gap-4">
            <div class="flex-shrink-0 w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <span class="text-3xl">👤</span>
            </div>
            <div class="flex-1 text-left">
              <h3 class="text-lg md:text-xl font-bold text-gray-800 dark:text-gray-100 mb-1">
                I'm an Individual
              </h3>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Personal account for individual use
              </p>
            </div>
            <div class="flex-shrink-0">
              <svg class="w-6 h-6 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </button>

        <!-- Business Account -->
        <button
          @click="selectBusiness"
          :disabled="isLoading"
          class="w-full group relative overflow-hidden rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 transition-all hover:border-purple-500 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div class="flex items-center gap-4">
            <div class="flex-shrink-0 w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <span class="text-3xl">🏢</span>
            </div>
            <div class="flex-1 text-left">
              <h3 class="text-lg md:text-xl font-bold text-gray-800 dark:text-gray-100 mb-1">
                I'm a Business
              </h3>
              <p class="text-sm text-gray-600 dark:text-gray-400">
                Business account for companies
              </p>
            </div>
            <div class="flex-shrink-0">
              <svg class="w-6 h-6 text-gray-400 group-hover:text-purple-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </button>
      </div>

      <!-- Loading Indicator -->
      <div v-if="isLoading" class="flex items-center gap-2 text-gray-600 dark:text-gray-400">
        <div class="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
        <span class="text-sm">Processing...</span>
      </div>
    </div>
  </CardLayout>
</template>

<style scoped lang="scss">
button:hover .group-hover\:scale-110 {
  transform: scale(1.1);
}
</style>
